import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { productApi } from '../services/api';
import { Product } from '../types';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [product, setProduct] = useState<Product | null>(null);
  const [others, setOthers] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProduct = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const res = await productApi.getById(id);
      setProduct(res.data);

      const all = await productApi.getAll();
      setOthers(all.data.filter((p: Product) => p._id !== res.data._id));
    } catch (error) {
      console.error('Error fetching product:', error);
      setProduct(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!product) {
    return (
      <div>
        <h2>Product not found</h2>
        <button onClick={() => navigate('/products')}>
          Back to Products
        </button>
      </div>
    );
  }

  return (
    <div>
      <Link 
        to="/products"
        style={{
          backgroundColor: '#6d9ac7',
          color: '#ffffff',
          border: 'none',
          padding: '0.75rem 1.5rem',
          borderRadius: '8px',
          cursor: 'pointer',
          marginBottom: '2rem',
          fontSize: '1rem',
          textDecoration: 'none',
          display: 'inline-block'
        }}
      >
        ← Back to Products
      </Link>
      
      <div style={{ 
        display: 'flex',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: '3 2 400px' }}>
          <img 
            src={product.image} 
            alt={product.name}
            style={{ 
              width: '80%', 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
          />
        </div>
        
        <div style={{ flex: '5 1 50px', marginLeft: '0.5rem' }}>
          <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem', color: '#303030' }}>
            {product.name}
          </h2>
          <p style={{ 
            color: '#6d9ac7', 
            fontSize: '1.1rem', 
            marginBottom: '1rem',
            fontWeight: '500'
          }}>
            {product.category}
          </p>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            marginBottom: '2rem'
          }}>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i}
                  size={20} 
                  fill={i < Math.floor(product.rating) ? "#51eefc" : "none"}
                  color="#51eefc"
                />
              ))}
            </div>
            <span style={{ color: '#303030', fontSize: '1.1rem' }}>
              {product.rating} / 5
            </span>
          </div>

          <p style={{ 
            fontSize: '1.1rem', 
            lineHeight: '1.6', 
            color: '#303030',
            marginBottom: '2rem'
          }}>
            {product.description}
          </p>

          <div style={{ 
            fontSize: '3rem', 
            fontWeight: 'bold', 
            color: '#1269cc',
            marginBottom: '2rem'
          }}>
            ${product.price}
          </div>

          <button 
            onClick={() => {
              alert('Cart feature removed');
            }}
            style={{
              backgroundColor: '#1269cc',
              color: '#ffffff',
              border: 'none',
              padding: '1rem 3rem',
              fontSize: '1.2rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
              width: '100%',
              maxWidth: '300px'
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#0d4f99')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#1269cc')}
          >
            Add to Cart
          </button>
        </div>
      </div>
      {/* Maybe You'll Like Section */}
      <section style={{ paddingTop: '3rem' }}>
        <h3 style={{ fontSize: '1.75rem', marginBottom: '1.5rem', color: '#303030', fontWeight: 'bold' }}>
          Maybe You'll Like
        </h3>
        <div style={{ 
          display: 'flex', 
          gap: '1.5rem',
          overflowX: 'auto',
          paddingBottom: '1rem'
        }}>
          {others
            .slice(0, 9)
            .map(p => (
              <div 
                key={p._id}
                style={{
                  minWidth: '280px',
                  flex: '0 0 280px',
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                }}
                onClick={() => navigate(`/product/${p._id}`)}
                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-8px)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <img 
                  src={p.image} 
                  alt={p.name}
                  style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                />
                    <div style={{ padding: '1.25rem' }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#303030', fontSize: '1.1rem' }}>
                        {p.name}
                      </h4>
                      <p style={{ color: '#6d9ac7', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        {p.category}
                      </p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#1269cc' }}>
                          ${p.price}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <Star size={16} fill="#51eefc" color="#51eefc" />
                          <span style={{ color: '#303030' }}>{p.rating}</span>
                        </div>
                      </div>
                    </div>
              </div>
            ))}
        </div>
      </section>
    </div>
  );
};

export default ProductDetailPage;